<template>
  <div
    class="automation-row"
    :class="{ 'automation-row--dragging': dragging }"
    @click="$emit('open', automation)"
  >
    <div class="automation-row__handle row-drag-handle" @click.stop>
      <BaseIcon name="grip-dots" size="14" />
    </div>
    <div class="automation-row__name">
      <span class="automation-row__title">{{ automation.name }}</span>
      <span class="automation-row__meta">{{ triggerLabel }}</span>
    </div>
    <div class="automation-row__cell automation-row__cell--muted automation-row__cell--num">
      {{ updatedLabel }}
    </div>
    <div class="automation-row__cell automation-row__cell--num">{{ stats.runs || 0 }}</div>
    <div class="automation-row__cell automation-row__cell--num automation-row__cell--success">
      {{ stats.succeeded || 0 }}
    </div>
    <div
      class="automation-row__cell automation-row__cell--num"
      :class="{ 'automation-row__cell--danger': stats.failed }"
    >
      {{ stats.failed || 0 }}
    </div>
    <div class="automation-row__cell automation-row__cell--status">
      <Label :variant="statusVariant" size="sm" show-dot>
        {{ statusLabel }}
      </Label>
    </div>
    <div class="automation-row__actions" @click.stop>
      <button
        class="automation-row__switch"
        :class="{ 'automation-row__switch--on': isActive }"
        type="button"
        role="switch"
        :aria-checked="isActive"
        :aria-label="
          isActive ? $t('automations_page.toggle_off') : $t('automations_page.toggle_on')
        "
        @click="$emit('toggle', automation)"
      >
        <span class="automation-row__switch-dot" />
      </button>
      <Btn
        variant="plain"
        size="sm"
        icon="trash"
        type="button"
        @click="$emit('delete', automation)"
      />
    </div>
  </div>
</template>

<script>
  import { computed, getCurrentInstance } from 'vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import Btn from '@/components/partials/Btn.vue';
  import Label from '@/components/partials/Label.vue';
  import { TRIGGER_TYPES } from '@/constants/automations';

  export default {
    name: 'AutomationRow',

    components: {
      BaseIcon,
      Btn,
      Label,
    },

    emits: ['open', 'toggle', 'delete'],

    props: {
      automation: {
        type: Object,
        required: true,
      },
      dragging: {
        type: Boolean,
        default: false,
      },
    },

    setup(props) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;

      const stats = computed(() => props.automation.stats || {});
      const isActive = computed(() => props.automation.status === 'active');

      const triggerLabel = computed(() => {
        const type = props.automation.trigger_type;
        return TRIGGER_TYPES.find((item) => item.value === type)?.label || type || '';
      });

      const updatedLabel = computed(() => {
        const value = props.automation.updated_at;
        if (!value) return '—';
        return new Date(value).toLocaleDateString('nl-NL', {
          day: 'numeric',
          month: 'short',
        });
      });

      const statusLabel = computed(() => {
        const status = props.automation.status || 'draft';
        return $t(`automations_page.status.${status}`) || status;
      });

      const statusVariant = computed(() => {
        if (props.automation.status === 'active') return 'success';
        if (props.automation.status === 'inactive') return 'default';
        return 'warning';
      });

      return {
        stats,
        isActive,
        triggerLabel,
        updatedLabel,
        statusLabel,
        statusVariant,
      };
    },
  };
</script>

<style scoped lang="scss">
  .automation-row {
    display: grid;
    grid-template-columns: var(--automation-row-grid, 24px minmax(0, 1fr) 88px 56px 56px 56px 88px 72px);
    align-items: center;
    gap: var(--automation-row-gap, 0.75rem);
    padding: 0.625rem var(--automation-row-pad-x, 0.75rem);
    background: #fff;
    cursor: pointer;
    border-bottom: 1px solid var(--slate-100);
    transition: background 0.15s ease;

    &:last-child {
      border-bottom: 0;
    }

    @include hover {
      background: var(--slate-50);
    }

    &--dragging {
      opacity: 0.55;
    }

    &__handle {
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--slate-400);
      cursor: grab;
    }

    &__name {
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 0.125rem;
    }

    &__title {
      font-size: 0.875rem;
      font-weight: 600;
      color: var(--slate-900);
      @include ellipsis(1);
    }

    &__meta {
      font-size: 0.75rem;
      color: var(--slate-500);
      @include ellipsis(1);
    }

    &__cell {
      font-size: 0.8125rem;
      color: var(--slate-700);

      &--muted {
        color: var(--slate-500);
      }

      &--num,
      &--status {
        text-align: right;
        font-variant-numeric: tabular-nums;
      }

      &--success {
        color: var(--green-600);
      }

      &--danger {
        color: var(--red-600);
        font-weight: 600;
      }
    }

    &__actions {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 0.375rem;
    }

    &__switch {
      position: relative;
      width: 36px;
      height: 21px;
      padding: 0;
      border: 0;
      border-radius: 9999px;
      background: var(--slate-200);
      cursor: pointer;
      flex-shrink: 0;
      transition: background-color 0.15s ease-out;

      &-dot {
        position: absolute;
        top: 50%;
        left: 4px;
        width: 14px;
        height: 14px;
        border-radius: 9999px;
        background: #fff;
        transform: translateY(-50%);
        transition: left 0.15s ease-out;
      }

      &--on {
        background: var(--brand-500);

        .automation-row__switch-dot {
          left: 18px;
        }
      }
    }
  }
</style>
